import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, PencilSimple, HandCoins, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const METODOS = ["transferencia", "efectivo", "bizum", "tarjeta", "otro"];

export default function Ingresos() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [docs, setDocs] = useState([]); // presupuestos + facturas
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const todayISO = new Date().toISOString().split("T")[0];
  const firstOfMonthISO = (() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split("T")[0];
  })();

  const [filters, setFilters] = useState({
    desde: firstOfMonthISO,
    hasta: todayISO,
    cliente_id: "todos",
    metodo_pago: "todos",
  });

  const empty = {
    fecha: todayISO,
    monto: 0,
    metodo_pago: "transferencia",
    descripcion: "",
    cliente_id: "none",
    documento_id: "none",
    documento_tipo: null,
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.desde) params.desde = filters.desde;
      if (filters.hasta) params.hasta = filters.hasta;
      if (filters.cliente_id && filters.cliente_id !== "todos") params.cliente_id = filters.cliente_id;
      if (filters.metodo_pago && filters.metodo_pago !== "todos") params.metodo_pago = filters.metodo_pago;
      const [r, c, presu, fact, res] = await Promise.all([
        api.get("/ingresos", { params }),
        api.get("/clientes"),
        api.get("/documentos", { params: { tipo: "presupuesto" } }),
        api.get("/documentos", { params: { tipo: "factura" } }),
        api.get("/ingresos/resumen", { params: { desde: filters.desde, hasta: filters.hasta } }),
      ]);
      setItems(r.data);
      setClientes(c.data);
      setDocs([...presu.data.map((d) => ({ ...d, _tipo: "presupuesto" })), ...fact.data.map((d) => ({ ...d, _tipo: "factura" }))]);
      setResumen(res.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const clienteMap = useMemo(() => Object.fromEntries(clientes.map((c) => [c.id, c])), [clientes]);
  const docMap = useMemo(() => Object.fromEntries(docs.map((d) => [d.id, d])), [docs]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      fecha: it.fecha,
      monto: it.monto,
      metodo_pago: it.metodo_pago || "transferencia",
      descripcion: it.descripcion || "",
      cliente_id: it.cliente_id || "none",
      documento_id: it.documento_id || "none",
      documento_tipo: it.documento_tipo || null,
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        fecha: form.fecha,
        monto: parseFloat(form.monto) || 0,
        metodo_pago: form.metodo_pago,
        descripcion: form.descripcion,
        cliente_id: form.cliente_id === "none" ? null : form.cliente_id,
        documento_id: form.documento_id === "none" ? null : form.documento_id,
        documento_tipo: form.documento_id === "none" ? null : (docMap[form.documento_id]?._tipo || null),
      };
      if (editing) {
        await api.put(`/ingresos/${editing.id}`, payload);
      } else {
        await api.post("/ingresos", payload);
      }
      toast.success(t("common.success"));
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("horas.confirmDelete"))) return;
    try {
      await api.delete(`/ingresos/${id}`);
      toast.success(t("common.success"));
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  // When a document is selected, auto-fill cliente_id
  const onDocChange = (did) => {
    if (did === "none") {
      setForm({ ...form, documento_id: "none", documento_tipo: null });
      return;
    }
    const d = docMap[did];
    setForm({
      ...form,
      documento_id: did,
      documento_tipo: d?._tipo || null,
      cliente_id: d?.cliente_id || form.cliente_id,
    });
  };

  const docLabel = (d) => `${d._tipo === "presupuesto" ? "Presu." : "Fact."} #${d.numero} — ${d.cliente_nombre || "—"} — € ${Number(d.total || 0).toFixed(2)}`;

  return (
    <div data-testid="ingresos-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl heading-font font-bold text-slate-900">{t("ingresos.title")}</h1>
          <p className="text-slate-500 mt-1 text-sm">{t("ingresos.subtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-green-600 hover:bg-green-700 text-white gap-2" data-testid="add-ingreso-btn">
              <Plus size={18} weight="bold" /> {t("ingresos.add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? t("ingresos.edit") : t("ingresos.add")}</DialogTitle>
              <DialogDescription className="sr-only">{t("ingresos.subtitle")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("ingresos.date")}</Label>
                  <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} required data-testid="ingreso-date-input" />
                </div>
                <div>
                  <Label>{t("ingresos.amount")} (€)</Label>
                  <Input type="number" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} required data-testid="ingreso-amount-input" />
                </div>
              </div>
              <div>
                <Label>{t("ingresos.method")}</Label>
                <Select value={form.metodo_pago} onValueChange={(v) => setForm({ ...form, metodo_pago: v })}>
                  <SelectTrigger data-testid="ingreso-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METODOS.map((m) => <SelectItem key={m} value={m}>{t(`ingresos.metodos.${m}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("ingresos.document")}</Label>
                <Select value={form.documento_id} onValueChange={onDocChange}>
                  <SelectTrigger data-testid="ingreso-doc-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("ingresos.noDoc")}</SelectItem>
                    {docs.map((d) => <SelectItem key={d.id} value={d.id}>{docLabel(d)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("ingresos.client")}</Label>
                <Select value={form.cliente_id} onValueChange={(v) => setForm({ ...form, cliente_id: v })}>
                  <SelectTrigger data-testid="ingreso-client-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("ingresos.description")}</Label>
                <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={2} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white" data-testid="ingreso-save-btn">{t("common.confirm")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary card */}
      {resumen && (
        <Card className="p-5 mb-5 border-green-200 bg-gradient-to-br from-green-50 to-white">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-100 text-green-700 flex items-center justify-center">
                <HandCoins size={24} weight="duotone" />
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{t("ingresos.totalReceived")}</div>
                <div className="text-2xl heading-font font-bold text-green-700">€ {resumen.total.toFixed(2)}</div>
                <div className="text-xs text-slate-500">{resumen.cantidad} {t("ingresos.entries")}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              {resumen.por_metodo.map((m) => (
                <div key={m.metodo} className="text-right">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{t(`ingresos.metodos.${m.metodo}`)}</div>
                  <div className="text-sm font-bold text-slate-900">€ {m.total.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4 mb-5 border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label>{t("reportes.from")}</Label>
            <Input type="date" value={filters.desde} onChange={(e) => setFilters({ ...filters, desde: e.target.value })} data-testid="ing-filter-from" />
          </div>
          <div>
            <Label>{t("reportes.to")}</Label>
            <Input type="date" value={filters.hasta} onChange={(e) => setFilters({ ...filters, hasta: e.target.value })} data-testid="ing-filter-to" />
          </div>
          <div>
            <Label>{t("ingresos.client")}</Label>
            <Select value={filters.cliente_id} onValueChange={(v) => setFilters({ ...filters, cliente_id: v })}>
              <SelectTrigger data-testid="ing-filter-client"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">{t("reportes.all")}</SelectItem>
                {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("ingresos.method")}</Label>
            <Select value={filters.metodo_pago} onValueChange={(v) => setFilters({ ...filters, metodo_pago: v })}>
              <SelectTrigger data-testid="ing-filter-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">{t("reportes.all")}</SelectItem>
                {METODOS.map((m) => <SelectItem key={m} value={m}>{t(`ingresos.metodos.${m}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} className="bg-green-600 hover:bg-green-700" data-testid="ing-apply-filter">{t("reportes.generate")}</Button>
        </div>
      </Card>

      {/* List */}
      {loading ? (
        <Card className="p-10 text-center text-slate-400">{t("common.loading")}</Card>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-slate-200">
          <HandCoins size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          {t("ingresos.empty")}
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const cli = clienteMap[it.cliente_id];
            const doc = docMap[it.documento_id];
            return (
              <Card key={it.id} className="p-4 border-slate-200 card-hover" data-testid={`ingreso-row-${it.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{it.fecha}</span>
                      <Badge className="bg-green-100 text-green-800 border-0 text-[10px]">{t(`ingresos.metodos.${it.metodo_pago}`)}</Badge>
                      {doc && (
                        <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] gap-1">
                          <LinkIcon size={10} weight="bold" /> {doc._tipo === "presupuesto" ? "Presu." : "Fact."} #{doc.numero}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-slate-900 mt-1 truncate">
                      {cli?.nome || "—"}
                      {it.descripcion && <span className="text-slate-500 font-normal"> · {it.descripcion}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl heading-font font-bold text-green-700">€ {Number(it.monto).toFixed(2)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(it)} data-testid={`edit-ingreso-${it.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(it.id)} data-testid={`delete-ingreso-${it.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
